import { useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { CustomField } from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { customFieldOptions } from '@/lib/custom-field-options';

// ============================================
// t43 (R13): Custom Field Filter Editor
// ============================================
//
// One filter row per active custom field. Renders:
//   - field name (left, fixed width)
//   - operator dropdown (changes per type)
//   - value input (changes per operator + type)
//
// The editor is fully controlled: parent owns the filter value
// shape `{ type, op, value, value2? }` and updates via onChange.

// t45 (R14): per-type accent colour for the field-name badge.
// Duplicated locally so this file has no dependency on task-filters.tsx.
const FIELD_TYPE_COLOR: Record<CustomField['type'], string> = {
    TEXT: '#60a5fa',
    NUMBER: '#a78bfa',
    DATE: '#f59e0b',
    SELECT: '#34d399',
    MULTI_SELECT: '#22d3ee',
    CHECKBOX: '#a3e635',
    URL: '#f472b6',
    EMAIL: '#fb923c',
    PERSON: '#c084fc',
    SPRINT: '#facc15',
};

// Operator keys per type — mirrors backend/src/services/custom-field-filter.service.ts.
// t43 (R13).
export const OPERATORS_BY_TYPE: Record<CustomField['type'], readonly string[]> = {
    TEXT: ['contains', 'notContains', 'equals', 'notEquals', 'startsWith', 'endsWith', 'isEmpty', 'isNotEmpty'],
    NUMBER: ['equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'between', 'isEmpty', 'isNotEmpty'],
    DATE: ['equals', 'before', 'after', 'between', 'isEmpty', 'isNotEmpty'],
    SELECT: ['is', 'isNot', 'isAnyOf', 'isNoneOf', 'isEmpty', 'isNotEmpty'],
    MULTI_SELECT: ['contains', 'notContains', 'containsAll', 'containsAny', 'isEmpty', 'isNotEmpty'],
    CHECKBOX: ['isTrue', 'isFalse'],
    URL: ['contains', 'equals', 'isEmpty', 'isNotEmpty'],
    EMAIL: ['contains', 'equals', 'isEmpty', 'isNotEmpty'],
    PERSON: ['is', 'isNot', 'isAnyOf', 'isNoneOf', 'isEmpty', 'isNotEmpty'],
    SPRINT: ['is', 'isNot', 'isAnyOf', 'isNoneOf', 'isEmpty', 'isNotEmpty'],
};

export interface CustomFieldFilterValue {
    type: CustomField['type'];
    op: string;
    value?: unknown;
    value2?: unknown;
}

interface CustomFieldFilterEditorProps {
    field: CustomField;
    value: CustomFieldFilterValue | null;
    onChange: (next: CustomFieldFilterValue | null) => void;
    /**
     * Project members for PERSON pickers. The editor only reads
     * `userId` + `user.name`, so any user-shaped object works.
     */
    members?: Array<{ userId: string; user: { id?: string; name: string; email?: string | null; avatarUrl?: string | null } }>;
}

// Helpers for option-id list picking
function asStringArray(v: unknown): string[] {
    if (Array.isArray(v)) {
        return v.filter((x): x is string => typeof x === 'string');
    }
    if (typeof v === 'string') return v ? [v] : [];
    return [];
}

export function CustomFieldFilterEditor({
    field,
    value,
    onChange,
    members = [],
}: CustomFieldFilterEditorProps) {
    const { t } = useI18n();
    const options = customFieldOptions(field);
    const op: string = value?.op || OPERATORS_BY_TYPE[field.type][0] || 'isEmpty';

    // Resolve the operator label via the typed t() — avoids the
    // `t(\`filter.operator.${op}\`)` interpolation which TS won't
    // narrow to the typed translation keys.
    const operatorLabel = (key: string): string => {
        switch (key) {
            case 'contains': return t('filter.operator.contains');
            case 'notContains': return t('filter.operator.notContains');
            case 'equals': return t('filter.operator.equals');
            case 'notEquals': return t('filter.operator.notEquals');
            case 'startsWith': return t('filter.operator.startsWith');
            case 'endsWith': return t('filter.operator.endsWith');
            case 'lt': return t('filter.operator.lt');
            case 'lte': return t('filter.operator.lte');
            case 'gt': return t('filter.operator.gt');
            case 'gte': return t('filter.operator.gte');
            case 'between': return t('filter.operator.between');
            case 'before': return t('filter.operator.before');
            case 'after': return t('filter.operator.after');
            case 'is': return t('filter.operator.is');
            case 'isNot': return t('filter.operator.isNot');
            case 'isAnyOf': return t('filter.operator.isAnyOf');
            case 'isNoneOf': return t('filter.operator.isNoneOf');
            case 'containsAll': return t('filter.operator.containsAll');
            case 'containsAny': return t('filter.operator.containsAny');
            case 'isEmpty': return t('filter.operator.isEmpty');
            case 'isNotEmpty': return t('filter.operator.isNotEmpty');
            case 'isTrue': return t('filter.operator.isTrue');
            case 'isFalse': return t('filter.operator.isFalse');
            default: return key;
        }
    };
    const NO_VALUE_OPS = new Set(['isEmpty', 'isNotEmpty', 'isTrue', 'isFalse']);
    const SINGLE_VALUE_OPS = new Set([
        'contains', 'notContains', 'equals', 'notEquals',
        'startsWith', 'endsWith',
        'lt', 'lte', 'gt', 'gte',
        'before', 'after',
        'is', 'isNot',
    ]);
    const BETWEEN_OPS = new Set(['between']);
    const MULTI_VALUE_OPS = new Set(['isAnyOf', 'isNoneOf', 'containsAll', 'containsAny']);

    const updateOp = (newOp: string) => {
        // Reset value when changing operators since the shape differs.
        onChange({ type: field.type, op: newOp });
    };

    // ---------- Render value input per (type, op) ----------
    const renderValueInput = () => {
        if (NO_VALUE_OPS.has(op)) return null;

        // TEXT / URL / EMAIL — text input
        if (field.type === 'TEXT' || field.type === 'URL' || field.type === 'EMAIL') {
            if (SINGLE_VALUE_OPS.has(op)) {
                return (
                    <Input
                        type={field.type === 'URL' ? 'url' : field.type === 'EMAIL' ? 'email' : 'text'}
                        className="h-8 w-40"
                        value={typeof value?.value === 'string' ? value.value : ''}
                        onChange={(e) => onChange({ type: field.type, op, value: e.target.value })}
                        placeholder={t('filter.valuePlaceholder.text')}
                    />
                );
            }
        }

        // NUMBER — number input (1 or 2 for between)
        if (field.type === 'NUMBER') {
            if (SINGLE_VALUE_OPS.has(op)) {
                return (
                    <Input
                        type="number"
                        className="h-8 w-28"
                        value={typeof value?.value === 'number' ? value.value : ''}
                        onChange={(e) =>
                            onChange({
                                type: field.type,
                                op,
                                value: e.target.value === '' ? undefined : Number(e.target.value),
                            })
                        }
                        placeholder={t('filter.valuePlaceholder.number')}
                    />
                );
            }
            if (BETWEEN_OPS.has(op)) {
                return (
                    <div className="flex items-center gap-1">
                        <Input
                            type="number"
                            className="h-8 w-24"
                            value={typeof value?.value === 'number' ? value.value : ''}
                            onChange={(e) =>
                                onChange({
                                    type: field.type,
                                    op,
                                    value: e.target.value === '' ? undefined : Number(e.target.value),
                                    value2: value?.value2,
                                })
                            }
                            placeholder={t('filter.valuePlaceholder.number')}
                        />
                        <span className="text-foreground-muted text-xs">→</span>
                        <Input
                            type="number"
                            className="h-8 w-24"
                            value={typeof value?.value2 === 'number' ? value.value2 : ''}
                            onChange={(e) =>
                                onChange({
                                    type: field.type,
                                    op,
                                    value: value?.value,
                                    value2: e.target.value === '' ? undefined : Number(e.target.value),
                                })
                            }
                            placeholder={t('filter.valuePlaceholder.number')}
                        />
                    </div>
                );
            }
        }

        // DATE — date input (1 or 2 for between)
        if (field.type === 'DATE') {
            if (SINGLE_VALUE_OPS.has(op)) {
                return (
                    <Input
                        type="date"
                        className="h-8 w-40"
                        value={typeof value?.value === 'string' ? value.value.slice(0, 10) : ''}
                        onChange={(e) => onChange({ type: field.type, op, value: e.target.value })}
                    />
                );
            }
            if (BETWEEN_OPS.has(op)) {
                return (
                    <div className="flex items-center gap-1">
                        <Input
                            type="date"
                            className="h-8 w-36"
                            value={typeof value?.value === 'string' ? value.value.slice(0, 10) : ''}
                            onChange={(e) =>
                                onChange({
                                    type: field.type,
                                    op,
                                    value: e.target.value,
                                    value2: value?.value2,
                                })
                            }
                        />
                        <span className="text-foreground-muted text-xs">→</span>
                        <Input
                            type="date"
                            className="h-8 w-36"
                            value={typeof value?.value2 === 'string' ? value.value2.slice(0, 10) : ''}
                            onChange={(e) =>
                                onChange({
                                    type: field.type,
                                    op,
                                    value: value?.value,
                                    value2: e.target.value,
                                })
                            }
                        />
                    </div>
                );
            }
        }

        // SELECT / PERSON / SPRINT — dropdown of options
        if (field.type === 'SELECT' || field.type === 'SPRINT') {
            if (SINGLE_VALUE_OPS.has(op)) {
                const singleValue = asStringArray(value?.value)[0] || '';
                return (
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 text-xs min-w-[120px]">
                                {singleValue
                                    ? options.find((o) => o.id === singleValue)?.label || singleValue
                                    : t('filter.valuePlaceholder.select')}
                                <ChevronDown className="ml-2 h-3 w-3" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="max-h-60 overflow-y-auto">
                            {options.map((opt) => (
                                <DropdownMenuItem
                                    key={opt.id}
                                    onClick={() => onChange({ type: field.type, op, value: opt.id })}
                                >
                                    {field.type === 'SPRINT' && (
                                        <span
                                            className="inline-block w-2 h-2 rounded-full shrink-0"
                                            style={{ backgroundColor: opt.color || '#6366f1' }}
                                        />
                                    )}
                                    <span>{opt.label}</span>
                                    {singleValue === opt.id && <span className="ml-auto">✓</span>}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            }
            if (MULTI_VALUE_OPS.has(op)) {
                const selectedIds = asStringArray(value?.value);
                return (
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 text-xs">
                                {selectedIds.length === 0
                                    ? t('filter.valuePlaceholder.selectMulti')
                                    : `${selectedIds.length} ${t('filter.selected')}`}
                                <ChevronDown className="ml-2 h-3 w-3" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56 p-2 max-h-60 overflow-y-auto">
                            {options.map((opt) => {
                                const checked = selectedIds.includes(opt.id);
                                return (
                                    <label
                                        key={opt.id}
                                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const next = checked
                                                ? selectedIds.filter((id) => id !== opt.id)
                                                : [...selectedIds, opt.id];
                                            onChange({ type: field.type, op, value: next });
                                        }}
                                    >
                                        <Checkbox checked={checked} />
                                        <span>{opt.label}</span>
                                    </label>
                                );
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            }
        }

        if (field.type === 'PERSON') {
            if (SINGLE_VALUE_OPS.has(op)) {
                const singleValue = asStringArray(value?.value)[0] || '';
                const member = members.find((m) => m.userId === singleValue);
                return (
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 text-xs min-w-[120px]">
                                {member?.user.name || t('filter.valuePlaceholder.selectPerson')}
                                <ChevronDown className="ml-2 h-3 w-3" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="max-h-60 overflow-y-auto">
                            {members.map((m) => (
                                <DropdownMenuItem
                                    key={m.userId}
                                    onClick={() => onChange({ type: field.type, op, value: m.userId })}
                                >
                                    <span>{m.user.name}</span>
                                    {singleValue === m.userId && <span className="ml-auto">✓</span>}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            }
            if (MULTI_VALUE_OPS.has(op)) {
                const selectedIds = asStringArray(value?.value);
                return (
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 text-xs">
                                {selectedIds.length === 0
                                    ? t('filter.valuePlaceholder.selectMulti')
                                    : `${selectedIds.length} ${t('filter.selected')}`}
                                <ChevronDown className="ml-2 h-3 w-3" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56 p-2 max-h-60 overflow-y-auto">
                            {members.map((m) => {
                                const checked = selectedIds.includes(m.userId);
                                return (
                                    <label
                                        key={m.userId}
                                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const next = checked
                                                ? selectedIds.filter((id) => id !== m.userId)
                                                : [...selectedIds, m.userId];
                                            onChange({ type: field.type, op, value: next });
                                        }}
                                    >
                                        <Checkbox checked={checked} />
                                        <span>{m.user.name}</span>
                                    </label>
                                );
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            }
        }

        // MULTI_SELECT — checkbox list (single or multi)
        if (field.type === 'MULTI_SELECT') {
            const SINGLE_MS = new Set(['contains', 'notContains']);
            const selectedIds = asStringArray(value?.value);
            if (SINGLE_MS.has(op)) {
                const singleValue = selectedIds[0] || '';
                return (
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 text-xs min-w-[120px]">
                                {options.find((o) => o.id === singleValue)?.label || t('filter.valuePlaceholder.select')}
                                <ChevronDown className="ml-2 h-3 w-3" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="max-h-60 overflow-y-auto">
                            {options.map((opt) => (
                                <DropdownMenuItem
                                    key={opt.id}
                                    onClick={() => onChange({ type: field.type, op, value: opt.id })}
                                >
                                    <span>{opt.label}</span>
                                    {singleValue === opt.id && <span className="ml-auto">✓</span>}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            }
            // MULTI_VALUE_OPS — checkbox list
            return (
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-xs">
                            {selectedIds.length === 0
                                ? t('filter.valuePlaceholder.selectMulti')
                                : `${selectedIds.length} ${t('filter.selected')}`}
                            <ChevronDown className="ml-2 h-3 w-3" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 p-2 max-h-60 overflow-y-auto">
                        {options.map((opt) => {
                            const checked = selectedIds.includes(opt.id);
                            return (
                                <label
                                    key={opt.id}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const next = checked
                                            ? selectedIds.filter((id) => id !== opt.id)
                                            : [...selectedIds, opt.id];
                                        onChange({ type: field.type, op, value: next });
                                    }}
                                >
                                    <Checkbox checked={checked} />
                                    <span>{opt.label}</span>
                                </label>
                            );
                        })}
                    </DropdownMenuContent>
                </DropdownMenu>
            );
        }

        return null;
    };

    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            {/* Field name — t45 (R14): the badge uses the field's
                type colour (text) so it inherits the same accent as
                the outer wrapper, reinforcing the chip's identity. */}
            <Badge
                variant="secondary"
                className="text-xs shrink-0 font-medium"
                style={{
                    color: FIELD_TYPE_COLOR[field.type] ?? undefined,
                    borderColor: FIELD_TYPE_COLOR[field.type] ?? undefined,
                }}
            >
                {field.name}
            </Badge>
            {/* Operator dropdown */}
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 text-xs">
                        {operatorLabel(op)}
                        <ChevronDown className="ml-1 h-3 w-3" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                    {OPERATORS_BY_TYPE[field.type].map((opKey) => (
                        <DropdownMenuItem key={opKey} onClick={() => updateOp(opKey)} className="py-1.5">
                            <span className="flex-1">{operatorLabel(opKey)}</span>
                            {op === opKey && <span className="ml-2 text-primary">✓</span>}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            {/* Value input (changes per operator) */}
            {renderValueInput()}
            {/* Clear button */}
            <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-foreground-muted hover:text-foreground"
                onClick={() => onChange(null)}
                title={t('filters.clearAll')}
            >
                <X className="h-3.5 w-3.5" />
            </Button>
        </div>
    );
}
